import React, { CSSProperties, FC, useEffect, useMemo } from "react";
import "./TabNavigator.css";
import { useState, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

interface ITabNavigatorProps {
    children: ReactNode;
    style?: CSSProperties;
    classBlock?: string | string[];
    queryParamName?: string;
    defaultTab?: number;
    callbackOnLoadFromQuery?: boolean;
    keepMounted?: boolean;
}

interface ICurrentTabProps {
    name: string;
    children: ReactNode;
    callBack?: () => void;
    style?: CSSProperties;
    className?: string;
    disabled?: boolean;
}

/**
 * Компонент вкладок для навигатора.
 *
 * @param {ReactNode} props.children - Дочерние элементы, которые представляют вкладки (`CurentTab`).
 * @param {() => void} callBack - Функция обратного вызова, которая будет выполнена при клике на вкладку.
 * @param {CSSProperties} [props.style] - Дополнительные стили, которые применяются к контейнеру.
 * @param {string} [props.className] - Класс контейнера.
 *
 * @returns {JSX.Element} - JSX-элемент, представляющий компонент навигации с вкладками.
 */
const CurrentTab: FC<ICurrentTabProps> = ({ children, callBack, style }) => {
    return (
        <div style={{ ...style }} onClick={callBack}>
            {children}
        </div>
    );
};

/**
 * Компонент для отображения навигации с помощью вкладок.
 *
 * @param {ITabNavigatorProps} props - Свойства компонента.
 * @param {ReactNode} props.children - Дочерние элементы, которые представляют вкладки (`CurentTab`).
 * @param {CSSProperties} [props.style] - Дополнительные стили, которые применяются к контейнеру.
 *
 * @returns {JSX.Element} - JSX-элемент, представляющий компонент навигации с вкладками.
 */
const TabNavigator: FC<ITabNavigatorProps> = ({
    children,
    style,
    classBlock,
    queryParamName,
    defaultTab,
    callbackOnLoadFromQuery,
    keepMounted
}) => {
    const [currentTab, setCurrentTab] = useState<number>(defaultTab ? defaultTab : 0);
    const [searchParams, setSearchParams] = useSearchParams();

    // Мемоизированный список вкладок
    const tabs = useMemo(() => {
        return React.Children.toArray(children).filter(
            (child) => React.isValidElement(child) && child.type === CurrentTab
        ) as React.ReactElement<ICurrentTabProps>[];
    }, [children]);

    // Обработчик клика по вкладке
    const handleTabClick = (index: number) => {
        if (tabs[index]?.props?.disabled) return;

        setCurrentTab(index);

        if (queryParamName) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set(queryParamName, String(index));
            setSearchParams(newParams);
        }

        tabs[index]?.props?.callBack?.();
    };

    // Инициализация текущей вкладки
    useEffect(() => {
        if (!queryParamName) return;

        const tabParam = searchParams.get(queryParamName);
        if (tabParam) {
            const tabIndex = Number(tabParam);
            if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabs.length) {
                setCurrentTab(tabIndex);
                if (callbackOnLoadFromQuery)
                    tabs[tabIndex]?.props?.callBack?.();
            }
        }
    }, [queryParamName, searchParams, tabs.length, defaultTab]);

    useEffect(() => {
        if (defaultTab !== undefined && defaultTab >= 0 && defaultTab < tabs.length) {
            setCurrentTab(defaultTab);
        }
    }, [defaultTab, tabs.length]);

    return (
        <div style={{ ...style }} className={`${classBlock ? classBlock : ""} ui__component--tab__navigator`}>
            <div className="ui__component--navigator">
                {tabs.map((tab, index) => (
                    <div
                        key={index}
                        className={`ui__component--navigator__tab ${index === currentTab ? "ui__component--navigator__tab--selected" : ""} ${tab.props.className ?? ''}`}
                        onClick={() => handleTabClick(index)}
                    >
                        <p>{tab.props.name}</p>
                    </div>
                ))}
            </div>
            <div className="tab-navigator__content">
                {keepMounted ? (
                    // Рендерим все вкладки, но скрываем неактивные
                    tabs.map((tab, index) => (
                        <div
                            className="tab-navigator__content-tab"
                            key={index}
                            style={{ display: index === currentTab ? 'block' : 'none' }}
                        >
                            {tab.props.children}
                        </div>
                    ))
                ) : (
                    // Рендерим только активную вкладку
                    currentTab !== undefined && tabs[currentTab] ? tabs[currentTab].props.children : null
                )}
            </div>
        </div>
    );
};

export { TabNavigator, CurrentTab };
